import { baseApi } from '@krp/client-runtime'

import { peekFrame } from '../../search/api/frames'
import type {
  TestingCheck,
  TestingFrameOutcome,
  TestingLane,
  TestingRun,
  TestingRunDetail,
  TestPhoto,
  TestProduct,
  TestProductDetail,
  TextCheck,
  TextQuery,
  TextQueryKind,
  TextRun,
  TextRunDetail,
} from '../model/types'
import {
  decodeTestingCheck,
  decodeTestingFrameOutcomes,
  decodeTestingRun,
  decodeTestingRunDetail,
  decodeTestingRuns,
  decodeTestPhoto,
  decodeTestProductDetail,
  decodeTestProducts,
  decodeGeneratedQueries,
  decodeTextCheck,
  decodeTextQueries,
  decodeTextQuery,
  decodeTextRun,
  decodeTextRunDetail,
  decodeTextRuns,
} from './contracts'
import { PLATFORM_TESTING_URLS, PLATFORM_TEXT_TESTING_URLS } from './urls'

/**
 * Запросы вкладки «Тестирование» (RP-FB29) к серверу замеров (RP-FB28). Команды
 * не повторяются transport-ом (небезопасный метод): неизвестный исход — решение
 * человека. Фото уходит multipart; файл берётся из реестра кадров по `frameId`,
 * в Redux попадает только ключ.
 */
export const PLATFORM_TESTING_TAG = 'PlatformTesting' as const

export interface SaveTestProductArgs {
  name: string
  sku: string | null
  barcodes: readonly string[]
}

export interface UpdateTestProductArgs extends SaveTestProductArgs {
  productId: string
  expectedVersion: number
}

export interface AddTestPhotoArgs {
  productId: string
  frameId: string
  role: 'reference' | 'frame'
  tags: Readonly<Record<string, string>>
}

export interface CheckStoredArgs {
  photoId: string
  lanes: TestingLane
}

const LIST = { type: PLATFORM_TESTING_TAG, id: 'PRODUCTS' } as const
const RUNS = { type: PLATFORM_TESTING_TAG, id: 'RUNS' } as const
const product = (id: string) => ({ type: PLATFORM_TESTING_TAG, id: `product:${id}` }) as const
const run = (id: string) => ({ type: PLATFORM_TESTING_TAG, id: `run:${id}` }) as const
const TEXT_QUERIES = { type: PLATFORM_TESTING_TAG, id: 'TEXT_QUERIES' } as const
const TEXT_RUNS = { type: PLATFORM_TESTING_TAG, id: 'TEXT_RUNS' } as const
const textRun = (id: string) => ({ type: PLATFORM_TESTING_TAG, id: `text-run:${id}` }) as const

export interface SaveTextQueryArgs {
  text: string
  productId: string
  kind: TextQueryKind
}

export const testingApi = baseApi.enhanceEndpoints({ addTagTypes: [PLATFORM_TESTING_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    platformTestingProducts: build.query<TestProduct[], void>({
      query: () => ({ url: PLATFORM_TESTING_URLS.products, decode: decodeTestProducts }),
      providesTags: [LIST],
    }),
    platformTestingProduct: build.query<TestProductDetail, { productId: string }>({
      query: ({ productId }) => ({ url: PLATFORM_TESTING_URLS.product(productId), decode: decodeTestProductDetail }),
      providesTags: (_result, _error, { productId }) => [product(productId)],
    }),
    platformTestingCreate: build.mutation<TestProductDetail, SaveTestProductArgs>({
      query: (body) => ({ url: PLATFORM_TESTING_URLS.products, method: 'POST', body, decode: decodeTestProductDetail }),
      invalidatesTags: [LIST],
    }),
    platformTestingCopyFromCatalog: build.mutation<TestProductDetail, { catalogProductId: string }>({
      query: ({ catalogProductId }) => ({
        url: PLATFORM_TESTING_URLS.fromCatalog,
        method: 'POST',
        body: { productId: catalogProductId },
        decode: decodeTestProductDetail,
      }),
      invalidatesTags: [LIST],
    }),
    platformTestingUpdate: build.mutation<TestProductDetail, UpdateTestProductArgs>({
      query: ({ productId, ...body }) => ({ url: PLATFORM_TESTING_URLS.product(productId), method: 'PUT', body, decode: decodeTestProductDetail }),
      invalidatesTags: [LIST],
      // Ответ правки — уже новая версия товара: сразу в кэш карточки, чтобы следующая правка несла её, а не прежнюю (иначе 409).
      onQueryStarted: async ({ productId }, { dispatch, queryFulfilled }): Promise<void> => {
        const data = await settled(queryFulfilled)
        if (data !== null) void dispatch(testingApi.util.upsertQueryData('platformTestingProduct', { productId }, data))
      },
    }),
    platformTestingDelete: build.mutation<null, { productId: string }>({
      query: ({ productId }) => ({ url: PLATFORM_TESTING_URLS.product(productId), method: 'DELETE' }),
      invalidatesTags: [LIST],
    }),
    platformTestingRefresh: build.mutation<TestProductDetail, { productId: string }>({
      query: ({ productId }) => ({ url: PLATFORM_TESTING_URLS.refresh(productId), method: 'POST', decode: decodeTestProductDetail }),
      invalidatesTags: [LIST],
      // Ответ — карточка после обновления из источника: сразу в кэш, без второго запроса за ней.
      onQueryStarted: async ({ productId }, { dispatch, queryFulfilled }): Promise<void> => {
        const data = await settled(queryFulfilled)
        if (data !== null) void dispatch(testingApi.util.upsertQueryData('platformTestingProduct', { productId }, data))
      },
    }),
    platformTestingReindex: build.mutation<TestProductDetail, { productId: string }>({
      query: ({ productId }) => ({ url: PLATFORM_TESTING_URLS.reindex(productId), method: 'POST', decode: decodeTestProductDetail }),
      invalidatesTags: [LIST],
      // Ответ — карточка с новым состоянием индекса: сразу в кэш, без второго запроса за ней.
      onQueryStarted: async ({ productId }, { dispatch, queryFulfilled }): Promise<void> => {
        const data = await settled(queryFulfilled)
        if (data !== null) void dispatch(testingApi.util.upsertQueryData('platformTestingProduct', { productId }, data))
      },
    }),
    platformTestingAddPhoto: build.mutation<TestPhoto, AddTestPhotoArgs>({
      query: ({ productId, frameId, role, tags }) => {
        const frame = peekFrame(frameId)
        if (frame === undefined) throw new Error('Фото уже снято с отправки: выберите его ещё раз')
        const form = new FormData()
        form.append('photo', frame, frame instanceof File ? frame.name : 'photo')
        form.append('role', role)
        for (const [axis, value] of Object.entries(tags)) if (value !== '') form.append(`tags[${axis}]`, value)
        return { url: PLATFORM_TESTING_URLS.photos(productId), method: 'POST', formData: form, decode: decodeTestPhoto }
      },
      invalidatesTags: (_result, _error, { productId }) => [LIST, product(productId)],
    }),
    platformTestingDeletePhoto: build.mutation<null, { productId: string; photoId: string }>({
      query: ({ photoId }) => ({ url: PLATFORM_TESTING_URLS.photo(photoId), method: 'DELETE' }),
      invalidatesTags: (_result, _error, { productId }) => [LIST, product(productId)],
    }),
    /** Проверка — вопрос к поиску, а не кэшируемое чтение: каждый раз заново, поэтому мутация. */
    platformTestingCheckStored: build.mutation<TestingCheck, CheckStoredArgs>({
      query: ({ photoId, lanes }) => ({
        url: PLATFORM_TESTING_URLS.checkStored(photoId),
        method: 'POST',
        params: { lanes },
        decode: decodeTestingCheck,
      }),
    }),
    platformTestingRuns: build.query<TestingRun[], void>({
      query: () => ({ url: PLATFORM_TESTING_URLS.runs, params: { take: '50' }, decode: decodeTestingRuns }),
      providesTags: [RUNS],
    }),
    platformTestingRun: build.query<TestingRunDetail, { runId: string }>({
      query: ({ runId }) => ({ url: PLATFORM_TESTING_URLS.run(runId), decode: decodeTestingRunDetail }),
      providesTags: (_result, _error, { runId }) => [run(runId)],
    }),
    platformTestingRunFrames: build.query<TestingFrameOutcome[], { runId: string }>({
      query: ({ runId }) => ({ url: PLATFORM_TESTING_URLS.runFrames(runId), decode: decodeTestingFrameOutcomes }),
      providesTags: (_result, _error, { runId }) => [run(runId)],
    }),
    platformTestingStartRun: build.mutation<TestingRun, { lanes: TestingLane }>({
      query: (body) => ({ url: PLATFORM_TESTING_URLS.runs, method: 'POST', body, decode: decodeTestingRun }),
      invalidatesTags: [RUNS],
    }),
    platformTestingCancelRun: build.mutation<TestingRun, { runId: string }>({
      query: ({ runId }) => ({ url: PLATFORM_TESTING_URLS.cancelRun(runId), method: 'POST', decode: decodeTestingRun }),
      invalidatesTags: (_result, _error, { runId }) => [RUNS, run(runId)],
    }),
    // --- поиск по тексту (RP-FB34) ---
    platformTextQueries: build.query<TextQuery[], void>({
      query: () => ({ url: PLATFORM_TEXT_TESTING_URLS.queries, decode: decodeTextQueries }),
      providesTags: [TEXT_QUERIES],
    }),
    platformTextAddQuery: build.mutation<TextQuery, SaveTextQueryArgs>({
      query: (body) => ({ url: PLATFORM_TEXT_TESTING_URLS.queries, method: 'POST', body, decode: decodeTextQuery }),
      invalidatesTags: [TEXT_QUERIES],
    }),
    platformTextDeleteQuery: build.mutation<null, { queryId: string }>({
      query: ({ queryId }) => ({ url: PLATFORM_TEXT_TESTING_URLS.query(queryId), method: 'DELETE' }),
      invalidatesTags: [TEXT_QUERIES],
    }),
    platformTextGenerate: build.mutation<{ removed: number; created: number; queries: TextQuery[] }, void>({
      query: () => ({ url: PLATFORM_TEXT_TESTING_URLS.generate, method: 'POST', decode: decodeGeneratedQueries }),
      invalidatesTags: [TEXT_QUERIES],
    }),
    /** Проверка — вопрос к поиску, а не кэшируемое чтение: каждый раз заново, поэтому мутация. */
    platformTextCheck: build.mutation<TextCheck, { query: string; expectedProductId: string | null }>({
      query: (body) => ({ url: PLATFORM_TEXT_TESTING_URLS.check, method: 'POST', body, decode: decodeTextCheck }),
    }),
    platformTextCheckStored: build.mutation<TextCheck, { queryId: string }>({
      query: ({ queryId }) => ({ url: PLATFORM_TEXT_TESTING_URLS.checkStored(queryId), method: 'POST', decode: decodeTextCheck }),
    }),
    platformTextRuns: build.query<TextRun[], void>({
      query: () => ({ url: PLATFORM_TEXT_TESTING_URLS.runs, params: { take: '50' }, decode: decodeTextRuns }),
      providesTags: [TEXT_RUNS],
    }),
    platformTextRun: build.query<TextRunDetail, { runId: string }>({
      query: ({ runId }) => ({ url: PLATFORM_TEXT_TESTING_URLS.run(runId), decode: decodeTextRunDetail }),
      providesTags: (_result, _error, { runId }) => [textRun(runId)],
    }),
    platformTextStartRun: build.mutation<TextRun, void>({
      query: () => ({ url: PLATFORM_TEXT_TESTING_URLS.runs, method: 'POST', decode: decodeTextRun }),
      invalidatesTags: [TEXT_RUNS],
    }),
    platformTextCancelRun: build.mutation<TextRun, { runId: string }>({
      query: ({ runId }) => ({ url: PLATFORM_TEXT_TESTING_URLS.cancelRun(runId), method: 'POST', decode: decodeTextRun }),
      invalidatesTags: (_result, _error, { runId }) => [TEXT_RUNS, textRun(runId)],
    }),
  }),
})

export const {
  platformTestingProducts,
  platformTestingProduct,
  platformTestingCreate,
  platformTestingCopyFromCatalog,
  platformTestingUpdate,
  platformTestingDelete,
  platformTestingRefresh,
  platformTestingReindex,
  platformTestingAddPhoto,
  platformTestingDeletePhoto,
  platformTestingCheckStored,
  platformTestingRuns,
  platformTestingRun,
  platformTestingRunFrames,
  platformTestingStartRun,
  platformTestingCancelRun,
  platformTextQueries,
  platformTextAddQuery,
  platformTextDeleteQuery,
  platformTextGenerate,
  platformTextCheck,
  platformTextCheckStored,
  platformTextRuns,
  platformTextRun,
  platformTextStartRun,
  platformTextCancelRun,
} = testingApi.endpoints

/** Данные ответа команды или null при отказе: отказ показывает сам результат команды, кэш тогда не трогается. */
async function settled<T>(queryFulfilled: Promise<{ data: T }>): Promise<T | null> {
  try {
    return (await queryFulfilled).data
  } catch {
    return null
  }
}
